import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const Outlet = ({
  editFormData,
  setEditFormData,
  materials,
  activeTab,
  targetAddress,
  variant = "default",
}) => {
  const { t, i18n } = useTranslation();
  const [selectedDestinations, setSelectedDestinations] = useState({});

  const sortedMaterials = useMemo(
    () =>
      [...materials].sort((a, b) =>
        t(a.referenceName).localeCompare(t(b.referenceName), i18n.language, {
          sensitivity: "base",
        }),
      ),
    [materials, t, i18n.language],
  );

  const handleCheckboxChange = (material, destination) => {
    setSelectedDestinations((prevState) => ({
      ...prevState,
      [material]: destination,
    }));
  };

  useEffect(
    () => {
      const newSelected = {};

      if (editFormData?.outlet && Object.keys(editFormData.outlet).length > 0) {
        Object.entries(editFormData.outlet).forEach(
          ([destination, materialsList]) => {
            materialsList.forEach((materialRef) => {
              newSelected[materialRef] = destination;
            });
          },
        );
      } else if (materials.length > 0) {
        materials.forEach((material) => {
          newSelected[material.referenceName] = "undefined";
        });
      }

      setSelectedDestinations((prev) => {
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(newSelected);
        return prevStr !== newStr ? newSelected : prev;
      });
    },
    // eslint-disable-next-line
    [targetAddress, activeTab],
  );

  useEffect(
    () => {
      let outletData = {
        landfill: [],
        energyRecovery: [],
        materialRecovery: [],
      };

      Object.keys(selectedDestinations).forEach((material) => {
        const destination = selectedDestinations[material];
        if (outletData[destination]) {
          outletData[destination].push(material);
        }
      });

      let newEditFormData = {
        ...editFormData,
        ...{ outlet: outletData },
      };

      setEditFormData(newEditFormData);
    },
    // eslint-disable-next-line
    [selectedDestinations],
  );

  const tableHeaders =
    variant === "site-treatment-table"
      ? {
          material: t("settingsSites.valoMaterialAbbr"),
          energy: t("settingsSites.valoEnergyAbbr"),
          landfill: t("settingsSites.storageAbbr"),
          undefined: t("settingsSites.undefinedAbbr"),
        }
      : {
          material: t("Material recovery"),
          energy: t("Energy recovery"),
          landfill: t("Landfill"),
          undefined: t("Undefined"),
        };

  return variant === "site-treatment-table" ? (
    <div className="w-full">
      <div className="sticky top-0 z-10 grid grid-cols-[2fr_1fr_1fr_1fr_1fr] h-[54px] border-y border-[#D3D3D3] bg-white text-[14px] font-bold text-[#222] shadow-[0_1px_0_#f0f0f0]">
        <div className="flex items-center px-8">{t("Type")}</div>
        <div className="flex items-center justify-center">
          {tableHeaders.material}
        </div>
        <div className="flex items-center justify-center">
          {tableHeaders.energy}
        </div>
        <div className="flex items-center justify-center">
          {tableHeaders.landfill}
        </div>
        <div className="flex items-center justify-center">
          {tableHeaders.undefined}
        </div>
      </div>
      {sortedMaterials.length > 0 &&
        sortedMaterials.map((material) => {
          const current = selectedDestinations[material.referenceName];
          const options = [
            "materialRecovery",
            "energyRecovery",
            "landfill",
            "undefined",
          ];
          return (
            <div
              key={`item-${material._id}`}
              className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] h-[56px] border-b border-[#D3D3D3] text-[14px] text-[#222]"
            >
              <div className="flex items-center px-8">
                {t(material.referenceName)}
              </div>
              {options.map((option) => {
                const isSelected =
                  option === "undefined"
                    ? current === "undefined" ||
                      ![
                        "materialRecovery",
                        "energyRecovery",
                        "landfill",
                      ].includes(current)
                    : current === option;
                return (
                  <div
                    key={option}
                    className="flex items-center justify-center"
                  >
                    <input
                      type="radio"
                      className="zone-settings__radio"
                      name={`outlet-${material.referenceName}`}
                      checked={isSelected}
                      onChange={() =>
                        handleCheckboxChange(material.referenceName, option)
                      }
                      aria-label={`${material.referenceName}-${option}`}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
    </div>
  ) : (
    <div className="flex flex-col ml-2 py-2">
      <div className="flex flex-row w-full mb-3">
        <div className="w-1/5"></div>
        <div className="w-1/5 text-center">
          <span className="font-bold">{t("Material recovery")}</span>
        </div>
        <div className="w-1/5 text-center">
          <span className="font-bold">{t("Energy recovery")}</span>
        </div>
        <div className="w-1/5 text-center">
          <span className="font-bold">{t("Landfill")}</span>
        </div>
        <div className="w-1/5 text-center">
          <span className="font-bold">{t("Undefined")}</span>
        </div>
      </div>
      {sortedMaterials.length > 0 &&
        sortedMaterials.map((material) => (
          <div
            key={`item-${material._id}`}
            className="flex flex-row w-full border-b-2 mb-1"
          >
            <div className="w-1/5">
              <span className="font-semibold">{t(material.referenceName)}</span>
            </div>
            <div className="w-1/5 text-center">
              <input
                className="relative h-5 p-0 w-auto"
                type="checkbox"
                checked={
                  selectedDestinations[material.referenceName] ===
                  "materialRecovery"
                }
                onChange={() =>
                  handleCheckboxChange(
                    material.referenceName,
                    "materialRecovery",
                  )
                }
              />
            </div>
            <div className="w-1/5 text-center">
              <input
                className="relative h-5 p-0 w-auto"
                type="checkbox"
                checked={
                  selectedDestinations[material.referenceName] ===
                  "energyRecovery"
                }
                onChange={() =>
                  handleCheckboxChange(material.referenceName, "energyRecovery")
                }
              />
            </div>
            <div className="w-1/5 text-center">
              <input
                className="relative h-5 p-0 w-auto"
                type="checkbox"
                checked={
                  selectedDestinations[material.referenceName] === "landfill"
                }
                onChange={() =>
                  handleCheckboxChange(material.referenceName, "landfill")
                }
              />
            </div>
            <div className="w-1/5 text-center">
              <input
                className="relative h-5 p-0 w-auto"
                type="checkbox"
                checked={
                  selectedDestinations[material.referenceName] ===
                    "undefined" ||
                  !["materialRecovery", "energyRecovery", "landfill"].includes(
                    selectedDestinations[material.referenceName],
                  )
                }
                onChange={() =>
                  handleCheckboxChange(material.referenceName, "undefined")
                }
              />
            </div>
          </div>
        ))}
    </div>
  );
};

export default Outlet;
